import { useQuery } from '@tanstack/react-query'
import {
  type ReactNode,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react'
import { agentEditorCopy } from '@/copy'
import { Alert } from '@/ui/Alert/Alert'
import { Avatar } from '@/ui/Avatar/Avatar'
import { Button } from '@/ui/Button/Button'
import { Card } from '@/ui/Card/Card'
import { Chip } from '@/ui/Chip/Chip'
import { EmptyState } from '@/ui/EmptyState/EmptyState'
import { Field, Input, Select, Textarea, TextField } from '@/ui/Field/Field'
import { Icon } from '@/ui/Icon/Icon'
import { Link } from '@/ui/Link/Link'
import { Skeleton, Spinner } from '@/ui/Loading/Loading'
import { useSkeletonDelay } from '@/ui/Loading/useSkeletonDelay'
import { Grid } from '@/ui/layout/Grid'
import { Inline } from '@/ui/layout/Inline'
import { Stack } from '@/ui/layout/Stack'
import { Text } from '@/ui/layout/Text'
import { RadioGroup } from '@/ui/RadioGroup/RadioGroup'
import { SectionHeader } from '@/ui/SectionHeader/SectionHeader'
import { lifecycleTone } from '@/ui/Status/lifecycle'
import { StatusDot } from '@/ui/Status/Status'
import { providersApi } from '../../api/agents'
import { api } from '../../api/client'
import { runsApi } from '../../api/workspaces'
import { ROUTES } from '../../constants/routes'
import {
  providerQueryKeys,
  useAgentPromptVersionsQuery,
} from '../../hooks/queries'
import { useAgentLiveStates } from '../../hooks/useAgentLiveState'
import { useDebouncedValue } from '../../hooks/useDebouncedValue'
import { useTopicsQuery } from '../../hooks/useTopicsQuery'
import type {
  AgentDefInfo,
  AvailableProviderInfo,
  CreateAgentDefDraftBody,
  RunInfo,
  UpdateAgentDefBody,
} from '../../types/api'
import type { FormState } from '../../types/editor'
import type { AgentCapabilityPreset } from '../../types/generated/AgentCapabilityPreset'
import { estimatePromptStats } from '../../utils/promptTokens'
import { CompactionConfigSection } from '../agent-editor/CompactionConfigSection'
import { computeAgentDiff } from '../agent-editor/diff'
import { ReasoningSection } from '../agent-editor/ReasoningSection'
import { ScheduleSection } from '../agent-editor/ScheduleSection'
import { DirtyDiffPanel, PromptHistoryList } from '../agent-editor/SharedPanels'
import { toCreateBody, toUpdateBody } from '../agent-editor/serialization'
import styles from './AgentV2Editor.module.css'
import { compilePreset, matchPreset } from './presets'
import { validateConfig } from './schemaValidation'
import { type AgentConfigV2Json, applyV2Json, toV2Json } from './v2Projection'

const PRESET_ORDER: AgentCapabilityPreset[] = [
  'readonly',
  'collab',
  'builder',
  'custom',
]

interface Props {
  agent: AgentDefInfo | null
  workspaceId: string
  providers: AvailableProviderInfo[]
  isPending: boolean
  error: string | null
  onSubmit: (body: CreateAgentDefDraftBody | UpdateAgentDefBody) => void
  onCancel: () => void
  /** Controlled form (owned by AgentFormEditor so it survives the
   * full-page prompt editor) — same contract as the v1 panel. */
  form: FormState
  set: (k: keyof FormState, v: FormState[keyof FormState]) => void
  base: FormState | null
  /** The full-page prompt editor; it replaces this entry (Done returns). */
  promptUrl?: string
  onRequestSaveTemplate?: () => void
  /** Existing agents: lifecycle toggle (submitted separately from config). */
  onTogglePause?: () => void
  pauseIsPending?: boolean
  /** Rendered first in the main column, ahead of the fields (new agents). */
  lead?: ReactNode
  /**
   * Bumped when the caller reseeds `form` (a template applied): the preset
   * and the JSON mirror restart from the new form. The editor stays mounted
   * so the `lead` (the template select) keeps focus.
   */
  seedKey?: number
}

/**
 * The redesigned agent editor (§6): essentials + capability presets +
 * the ADVANCED · JSON editor validated against the served agent-config
 * v2 schema. v2 is a projection — saving compiles to the existing wire
 * body (plus the persisted preset label) through the same submission
 * machinery as v1 (idempotent create, 202 reconcile, topic origin).
 */
export function AgentV2Editor({
  agent,
  workspaceId,
  providers,
  isPending,
  error,
  onSubmit,
  onCancel,
  form,
  set,
  base,
  promptUrl,
  onRequestSaveTemplate,
  onTogglePause,
  pauseIsPending,
  lead,
  seedKey = 0,
}: Props) {
  const seedPreset = (): AgentCapabilityPreset =>
    (agent?.capabilities_preset as AgentCapabilityPreset | undefined) ??
    matchPreset(
      form.capabilities
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean),
    )
  const [preset, setPreset] = useState<AgentCapabilityPreset>(seedPreset)
  const [presetSeed, setPresetSeed] = useState(seedKey)
  if (presetSeed !== seedKey) {
    setPresetSeed(seedKey)
    setPreset(seedPreset())
  }
  const [jsonText, setJsonText] = useState('')
  // The seed the JSON was hand-edited under; a new seed regenerates it.
  const jsonEditedRef = useRef<number | null>(null)
  const promptHeadingId = useId()

  // ── Schema + validation ──────────────────────────────────────────────
  const { data: schema } = useQuery({
    queryKey: ['agent-config-schema'],
    queryFn: () => api.get<unknown>('/api/agent-config-schema'),
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
  })

  const status = useMemo(() => {
    let parsed: unknown
    try {
      parsed = JSON.parse(jsonText)
    } catch (e) {
      return {
        kind: 'invalid' as const,
        issues: [
          {
            path: '',
            message:
              e instanceof Error
                ? e.message
                : agentEditorCopy.editor.invalidJson,
          },
        ],
      }
    }
    if (!schema) {
      // Schema not loaded yet: accept well-formed JSON, mark no status.
      return { kind: 'pending' as const, warnings: [] }
    }
    return validateConfig(schema, parsed)
  }, [jsonText, schema])

  // ── JSON ↔ form sync ─────────────────────────────────────────────────
  // While the user hasn't hand-edited the JSON, regenerate it from the
  // form. Once edited, the JSON is the source of truth: valid edits
  // apply back into the form, and hand-edited capability lines flip the
  // preset to `custom`.
  useEffect(() => {
    if (jsonEditedRef.current !== seedKey) {
      setJsonText(`${JSON.stringify(toV2Json(form, preset), null, 2)}\n`)
    }
  }, [form, preset, seedKey])

  function handleJsonChange(text: string) {
    jsonEditedRef.current = seedKey
    setJsonText(text)
    let parsed: AgentConfigV2Json
    try {
      parsed = JSON.parse(text) as AgentConfigV2Json
    } catch {
      return
    }
    if (!schema) return
    if (validateConfig(schema, parsed).kind === 'invalid') return
    const { patch, preset: claimed } = applyV2Json(parsed)
    for (const key of Object.keys(patch) as (keyof FormState)[]) {
      set(key, patch[key] as FormState[keyof FormState])
    }
    // Hand-edited capability lines that no longer match the claimed
    // preset flip to custom (design §6).
    const claimedLines = claimed !== 'custom' ? compilePreset(claimed) : null
    const lines = (parsed.capabilities ?? []).slice().sort().join('\n')
    setPreset(
      claimedLines && [...claimedLines].sort().join('\n') !== lines
        ? 'custom'
        : claimed,
    )
  }

  function handlePresetChip(next: Exclude<AgentCapabilityPreset, 'custom'>) {
    setPreset(next)
    set('capabilities', compilePreset(next).join('\n'))
  }

  // ── Sidebar data ─────────────────────────────────────────────────────
  const { stateOf } = useAgentLiveStates(workspaceId)
  const {
    data: topics = [],
    isLoading: topicsLoading,
    isError: topicsError,
    refetch: refetchTopics,
  } = useTopicsQuery(workspaceId)
  const memberTopics = agent
    ? topics.filter((t) => t.agents.some((a) => a.agent_id === agent.agent_id))
    : []
  const { data: versions = [] } = useAgentPromptVersionsQuery(
    agent?.workspace_id ?? '',
    agent?.agent_id ?? '',
    !!agent,
  )
  const {
    data: runs = [],
    isLoading: runsLoading,
    isError: runsError,
    refetch: refetchRuns,
  } = useQuery<RunInfo[]>({
    queryKey: ['runs', workspaceId],
    queryFn: async () =>
      (await runsApi.listWorkspaceRuns(workspaceId, { limit: 100 })).runs,
    enabled: !!agent && !!workspaceId,
    staleTime: 30_000,
  })
  const runsPerDay = useMemo(() => {
    if (!agent) return []
    const mine = runs.filter(
      (r) => r.agent_id === agent.agent_id && r.finished_at != null,
    )
    const byDay = new Map<string, number>()
    for (const r of mine) {
      const day = new Date(r.finished_at as number).toLocaleDateString(
        undefined,
        { month: 'short', day: 'numeric' },
      )
      byDay.set(day, (byDay.get(day) ?? 0) + 1)
    }
    return [...byDay.entries()].slice(0, 7)
  }, [runs, agent])

  // ── Provider/model plumbing (mirrors v1) ────────────────────────────
  const { data: fetchedModels = [] } = useQuery({
    queryKey: providerQueryKeys.models(workspaceId, form.provider_id),
    queryFn: () => providersApi.listModels(form.provider_id, { workspaceId }),
    enabled: !!form.provider_id,
    staleTime: 5 * 60_000,
    retry: false,
  })
  /* eslint-disable react-hooks/exhaustive-deps -- intentional: only react to fetchedModels changes */
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional — only react to fetchedModels changes
  useEffect(() => {
    if (
      fetchedModels.length > 0 &&
      form.model &&
      !fetchedModels.includes(form.model)
    ) {
      set('model', '')
    }
  }, [fetchedModels])
  /* eslint-enable react-hooks/exhaustive-deps */
  // The model's reasoning control (a catalog lookup, no vendor call), which
  // drives the reasoning section. Keyed on the free-text model field, so it
  // waits for typing to pause rather than fetching per keystroke.
  const model = useDebouncedValue(form.model.trim(), 300)
  const {
    data: profile,
    error: profileError,
    isPending: profilePending,
    refetch: refetchProfile,
  } = useQuery({
    queryKey: providerQueryKeys.profile(workspaceId, form.provider_id, model),
    queryFn: () =>
      providersApi.modelProfile(form.provider_id, model, { workspaceId }),
    enabled: !!form.provider_id && !!model,
    staleTime: 5 * 60_000,
    retry: false,
  })

  const diff = useMemo(
    () => (base ? computeAgentDiff(base, form) : null),
    [base, form],
  )
  const promptStats = useMemo(
    () => estimatePromptStats(form.system_prompt),
    [form.system_prompt],
  )

  useEffect(() => {
    if (!agent && !form.provider_id && providers.length === 1) {
      set('provider_id', providers[0].provider_id)
    }
  }, [agent, form.provider_id, providers, set])

  const missingProvider =
    !agent && providers.length > 0 && !form.provider_id.trim()
  const liveState = agent ? stateOf(agent.agent_id) : null
  const showTopicsSkeleton = useSkeletonDelay(topicsLoading)
  const showRunsSkeleton = useSkeletonDelay(runsLoading)
  const invalid = status.kind === 'invalid'
  const saveBlocked = invalid || missingProvider
  const statusText =
    status.kind === 'invalid'
      ? (status.issues[0]?.message ?? agentEditorCopy.editor.invalidConfig)
      : status.kind === 'valid'
        ? status.warnings.length > 0
          ? agentEditorCopy.editor.warning(status.warnings[0].message)
          : agentEditorCopy.editor.conforms
        : null

  function submit(e: React.FormEvent) {
    e.preventDefault()
    if (saveBlocked || isPending) return
    if (agent) {
      onSubmit({
        ...toUpdateBody(form, agent.reasoning),
        capabilities_preset: preset,
      })
    } else {
      onSubmit({
        ...toCreateBody(form),
        capabilities_preset: preset,
      })
    }
  }

  const firstTopic = memberTopics[0]

  // D188: the Retry for a failed load sits inside its Alert.
  const loadFailed = (message: string, refetch: () => unknown) => (
    <Alert tone="danger">
      <Inline gap="space-8" align="baseline">
        <Text>{message}</Text>
        <Button variant="outline" size="sm" onClick={() => void refetch()}>
          {agentEditorCopy.retry}
        </Button>
      </Inline>
    </Alert>
  )

  return (
    <Stack as="form" gap="space-16" onSubmit={submit}>
      {agent && (
        <Inline justify="between" gap="space-12">
          <Inline gap="space-12">
            <Avatar name={form.name || agent.name} kind="agent" size="lg" />
            <Stack gap="space-4">
              <Text as="p" variant="heading">
                {form.name || agent.name}
              </Text>
              <Inline gap="space-8">
                <Text tone="text-sub">
                  <StatusDot
                    tone={lifecycleTone(agent.status)}
                    label={agent.status}
                  />
                </Text>
                {liveState ? (
                  <Text tone="text-sub">
                    <StatusDot
                      tone={lifecycleTone(liveState)}
                      label={liveState}
                    />
                  </Text>
                ) : (
                  <Text variant="caption" tone="text-ghost">
                    —
                  </Text>
                )}
              </Inline>
            </Stack>
          </Inline>
          <Inline gap="space-8">
            {onTogglePause && (
              <Button
                variant="outline"
                loading={pauseIsPending}
                onClick={onTogglePause}
              >
                {agent.status === 'paused'
                  ? agentEditorCopy.editor.activate
                  : agentEditorCopy.editor.pause}
              </Button>
            )}
            {firstTopic && (
              <Button
                variant="outline"
                href={ROUTES.topic(firstTopic.topic_id)}
              >
                {agentEditorCopy.editor.message}
              </Button>
            )}
          </Inline>
        </Inline>
      )}

      <div className={agent ? styles.grid : undefined}>
        <Stack gap="space-16" className={styles.column}>
          {lead}
          <Card>
            <Stack gap="space-16">
              <SectionHeader>{agentEditorCopy.editor.essentials}</SectionHeader>
              <Grid columns={1} mdColumns={2} gap="space-16">
                <Field
                  id="af-name"
                  label={agentEditorCopy.editor.nameLabel}
                  hint={agentEditorCopy.schedule.requiredHint}
                  required
                >
                  <Input
                    value={form.name}
                    onChange={(e) => set('name', e.target.value)}
                  />
                </Field>
                <Field id="af-model" label={agentEditorCopy.editor.modelLabel}>
                  <Input
                    list="af-models"
                    placeholder={agentEditorCopy.editor.modelPlaceholder}
                    value={form.model}
                    onChange={(e) => set('model', e.target.value)}
                  />
                  <datalist id="af-models">
                    {fetchedModels.map((m) => (
                      <option key={m} value={m} />
                    ))}
                  </datalist>
                </Field>
              </Grid>
              <TextField
                id="af-description"
                label={agentEditorCopy.description}
                value={form.description}
                onValue={(v) => set('description', v)}
              />
              <Field
                id="af-provider"
                label={agentEditorCopy.editor.providerLabel}
                help={
                  missingProvider
                    ? agentEditorCopy.editor.selectProvider
                    : undefined
                }
              >
                <Select
                  value={form.provider_id}
                  onChange={(e) => set('provider_id', e.target.value)}
                >
                  <option value="">—</option>
                  {providers.map((p) => (
                    <option key={p.provider_id} value={p.provider_id}>
                      {p.name || p.provider_id}
                    </option>
                  ))}
                </Select>
              </Field>
            </Stack>
          </Card>

          <Card>
            <Stack gap="space-16">
              <Inline gap="space-8">
                <Inline gap="space-8" align="baseline" className={styles.grow}>
                  <div className={styles.grow}>
                    <Text as="h2" id={promptHeadingId} weight="strong">
                      {agentEditorCopy.editor.systemPrompt}
                    </Text>
                  </div>
                  <Text variant="caption" tone="text-mute" numeric>
                    {agentEditorCopy.editor.aboutTokens(promptStats.tokens)}
                  </Text>
                </Inline>
                {promptUrl && (
                  <Button
                    variant="ghost"
                    ground="surface"
                    href={promptUrl}
                    replace
                  >
                    <Icon name="external-link" size="icon-embedded" />
                    {agentEditorCopy.editor.expand}
                  </Button>
                )}
              </Inline>
              <Textarea
                aria-labelledby={promptHeadingId}
                rows={8}
                value={form.system_prompt}
                onChange={(e) => set('system_prompt', e.target.value)}
              />
              {versions.length > 0 && (
                <PromptHistoryList
                  versions={versions}
                  currentPrompt={form.system_prompt}
                  onRestore={(p) => set('system_prompt', p)}
                />
              )}
            </Stack>
          </Card>

          <Card>
            <ReasoningSection
              profile={form.provider_id && model ? profile : undefined}
              value={form.reasoning}
              onChange={(r) => set('reasoning', r)}
              notice={agent?.reasoning_notice}
              error={profileError}
              loading={!!form.provider_id && !!model && profilePending}
              onRetry={() => void refetchProfile()}
            />
          </Card>

          <Card>
            <RadioGroup
              label={agentEditorCopy.editor.capabilities}
              value={preset}
              onChange={(v) =>
                v !== 'custom' &&
                handlePresetChip(v as Exclude<AgentCapabilityPreset, 'custom'>)
              }
              options={PRESET_ORDER.map((p) => ({
                value: p,
                label: agentEditorCopy.presets.label[p],
                description: agentEditorCopy.presets.summary[p],
                disabled: p === 'custom',
              }))}
            />
          </Card>

          <Card>
            <CompactionConfigSection
              state={form.compaction}
              onChange={(c) => set('compaction', c)}
            />
          </Card>

          <Card>
            <Stack gap="space-16">
              <SectionHeader>
                {agentEditorCopy.editor.advancedJson}
              </SectionHeader>
              <Field
                id="af-json"
                label={agentEditorCopy.editor.jsonLabel}
                error={invalid ? statusText : undefined}
                help={
                  invalid ? undefined : statusText ? (
                    <>
                      <Icon
                        name={
                          status.kind === 'valid' && status.warnings.length > 0
                            ? 'exclamation-triangle'
                            : 'check'
                        }
                        size="icon-embedded"
                      />{' '}
                      {statusText}
                    </>
                  ) : (
                    <Spinner />
                  )
                }
              >
                <Textarea
                  mono
                  rows={16}
                  wrap="off"
                  spellCheck={false}
                  value={jsonText}
                  onChange={(e) => handleJsonChange(e.target.value)}
                />
              </Field>
            </Stack>
          </Card>

          <Card>
            <ScheduleSection
              entries={form.schedule}
              onChange={(s) => set('schedule', s)}
            />
          </Card>

          {error && <Alert tone="danger">{error}</Alert>}
          {diff && <DirtyDiffPanel diff={diff} />}
          <Inline gap="space-8">
            <Button
              variant="primary"
              type="submit"
              disabled={saveBlocked}
              loading={isPending}
            >
              {agent
                ? agentEditorCopy.editor.save
                : agentEditorCopy.editor.createAgent}
            </Button>
            {onRequestSaveTemplate && (
              <Button variant="outline" onClick={onRequestSaveTemplate}>
                {agentEditorCopy.editor.saveAsTemplate}
              </Button>
            )}
            <Button variant="outline" onClick={onCancel} disabled={isPending}>
              {agentEditorCopy.editor.cancel}
            </Button>
          </Inline>
        </Stack>

        {agent && (
          <Stack as="aside" gap="space-16">
            {agent && (
              <Card>
                <Stack gap="space-12">
                  <SectionHeader>
                    {agentEditorCopy.editor.memberOf}
                  </SectionHeader>
                  {topicsLoading ? (
                    <Stack gap="space-8" aria-busy="true">
                      {showTopicsSkeleton && (
                        <Skeleton lines={2} ground="surface" />
                      )}
                    </Stack>
                  ) : topicsError ? (
                    loadFailed(agentEditorCopy.loadTopicsFailed, refetchTopics)
                  ) : memberTopics.length === 0 ? (
                    <EmptyState
                      variant="inline"
                      description={agentEditorCopy.editor.noTopics}
                    />
                  ) : (
                    <Stack as="ul" gap="space-6">
                      {memberTopics.map((t) => (
                        <Inline as="li" key={t.topic_id} gap="space-8">
                          <Link href={ROUTES.topic(t.topic_id)}>#{t.name}</Link>
                          {t.primary_agent_id === agent.agent_id && (
                            <Chip
                              variant="kind"
                              label={agentEditorCopy.editor.primary}
                            />
                          )}
                        </Inline>
                      ))}
                    </Stack>
                  )}
                </Stack>
              </Card>
            )}

            {agent && (
              <Card>
                <Stack gap="space-12">
                  <SectionHeader>
                    {agentEditorCopy.editor.activity}
                  </SectionHeader>
                  {runsLoading ? (
                    <Stack gap="space-8" aria-busy="true">
                      {showRunsSkeleton && (
                        <Skeleton lines={2} ground="surface" />
                      )}
                    </Stack>
                  ) : runsError ? (
                    loadFailed(agentEditorCopy.loadRunsFailed, refetchRuns)
                  ) : runsPerDay.length === 0 ? (
                    <EmptyState
                      variant="inline"
                      description={agentEditorCopy.editor.noRuns}
                    />
                  ) : (
                    <Stack as="ul" gap="space-6">
                      {runsPerDay.map(([day, count]) => (
                        <Inline
                          as="li"
                          key={day}
                          justify="between"
                          gap="space-8"
                        >
                          <Text variant="caption" tone="text-mute" numeric>
                            {day}
                          </Text>
                          <Text variant="caption" tone="text-mute" numeric>
                            {agentEditorCopy.editor.runsCount(count)}
                          </Text>
                        </Inline>
                      ))}
                    </Stack>
                  )}
                  <Link href={ROUTES.workspaceRuns(workspaceId)}>
                    {agentEditorCopy.editor.allRuns}
                  </Link>
                </Stack>
              </Card>
            )}
          </Stack>
        )}
      </div>
    </Stack>
  )
}
